/**
 * Firebase wiring for the LDG Hub.
 *
 * Reads the project config from VITE_FIREBASE_* env vars (set in the Hub
 * Netlify site, not bundled into the source). Initializes the SDK exactly
 * once per app load and exports the handles the rest of the Hub uses.
 *
 * Failure modes:
 *   - missing env vars (forgot to set them in Netlify) -> `state` is
 *     'missing-env', exports remain null, UI shows a clear error so we never
 *     silently fail in a way that looks "almost working"
 *   - SDK init throws -> `state` is 'error' with the message exposed
 *
 * The handles (app/db/auth) intentionally use null-or-real typing rather
 * than throwing during import — that way the landing page (and the
 * Phase 0 status indicator) can render even if env is misconfigured,
 * which is the exact case we need to diagnose.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getToken as getAppCheckToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
  setTokenAutoRefreshEnabled,
  type AppCheck,
} from 'firebase/app-check';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import {
  appCheckTokenShareable,
  createLazyStart,
  createTokenDeadline,
  registerActiveTokenDeadline,
  type TokenDeadline,
} from './appCheckDeadline';
import { resolveAuthDomain } from './authSignInPolicy';

type AppCheckProvider = 'recaptcha-enterprise' | 'recaptcha-v3';

type AppCheckStatus =
  | { state: 'disabled'; reason: 'missing-site-key' | 'server-render' }
  | { state: 'ready'; provider: AppCheckProvider }
  | { state: 'error'; message: string };

export type FirebaseStatus =
  | { state: 'missing-env'; missingKeys: string[] }
  | { state: 'ready'; projectId: string; appCheck: AppCheckStatus }
  | { state: 'error'; message: string };

interface FirebaseHandles {
  app: FirebaseApp | null;
  appCheck: AppCheck | null;
  db: Firestore | null;
  auth: Auth | null;
  storage: FirebaseStorage | null;
  status: FirebaseStatus;
}

function appCheckProviderName(): AppCheckProvider {
  return import.meta.env.VITE_FIREBASE_APPCHECK_PROVIDER === 'recaptcha-v3'
    ? 'recaptcha-v3'
    : 'recaptcha-enterprise';
}

/**
 * The reCAPTCHA providers with a time limit on each token request, so a
 * reCAPTCHA download that never finishes cannot freeze Firestore, Auth and
 * everything behind them. The why, the proof, who needs a real token and the
 * enforcement caveat are all in lib/appCheckDeadline.ts; read it before
 * changing this.
 *
 * App Check calls a provider's getToken() for every fresh token. That method
 * is real and stable (it is the contract CustomProvider implements too) but
 * is left out of the SDK's published types, hence the cast to reach the
 * parent's copy. Everything else, including initialize() and isEqual(), is
 * inherited untouched, and isEqual()'s instanceof check still matches the
 * parent class. tests/app-check-deadline.test.mjs pins that the SDK still
 * ships getToken on these prototypes, so an upgrade that renames it fails a
 * test instead of silently dropping the limit.
 *
 * A LIMITED-USE token request (getToken(true), only reached through App
 * Check's getLimitedUseToken, which nothing in the Hub calls today) goes
 * straight to the parent, untouched: it is a separate one-off token and must
 * never share, or be answered by, the regular attempt.
 */
type ProviderTokenSource = { getToken(this: unknown, ...args: unknown[]): Promise<unknown> };

function createHubTokenDeadline(): TokenDeadline<unknown> {
  const deadline = createTokenDeadline<unknown>({
    usable: (value) => appCheckTokenShareable(value, Date.now()),
    // A real token has arrived after the deadline: ask App Check for one now,
    // which collects it, stores it and tells Firestore and Auth, instead of
    // leaving them on the placeholder until App Check's next retry.
    onLateResult: () => {
      const instance = (globalThis as typeof globalThis & { __ldgHubAppCheckInstance?: AppCheck })
        .__ldgHubAppCheckInstance;
      if (instance) void getAppCheckToken(instance, false).catch(() => {});
    },
  });
  // So lib/functionSecurity.ts can wait on it (waitForAppCheckAttempt).
  registerActiveTokenDeadline(deadline);
  return deadline;
}

type ProviderInitialize = { initialize(this: unknown, app: FirebaseApp): void };

/**
 * App Check starts with its background refresh OFF (so initializeAppCheck()
 * does not fetch a token, and so load reCAPTCHA, at import time) and this
 * turns it on, once: when the provider is first asked for a token, and when a
 * signed-in person's session is restored (lib/firebase.ts below). From then
 * on it keeps the token fresh ahead of expiry exactly as before. A signed-out
 * visitor or a dancer whose page needs no token never pays for reCAPTCHA.
 */
let autoRefreshOn = false;
function enableAppCheckAutoRefresh(): void {
  if (autoRefreshOn) return;
  const instance = (globalThis as typeof globalThis & { __ldgHubAppCheckInstance?: AppCheck })
    .__ldgHubAppCheckInstance;
  if (!instance) return;
  autoRefreshOn = true;
  setTokenAutoRefreshEnabled(instance, true);
}

class BoundedReCaptchaEnterpriseProvider extends ReCaptchaEnterpriseProvider {
  private readonly hubTokenDeadline = createHubTokenDeadline();
  private readonly hubLazyStart = createLazyStart();

  // Held, not run: this is what adds Google's script to the page
  // (createLazyStart in lib/appCheckDeadline.ts).
  initialize(app: FirebaseApp): void {
    const parentInitialize = (ReCaptchaEnterpriseProvider.prototype as unknown as ProviderInitialize).initialize;
    this.hubLazyStart.hold(() => parentInitialize.call(this, app));
  }

  getToken(...args: unknown[]): Promise<unknown> {
    if (this.hubLazyStart.release()) queueMicrotask(enableAppCheckAutoRefresh);
    const parentGetToken = (ReCaptchaEnterpriseProvider.prototype as unknown as ProviderTokenSource).getToken;
    if (args[0] === true) return parentGetToken.apply(this, args);
    return this.hubTokenDeadline.run(() => parentGetToken.apply(this, args));
  }
}

class BoundedReCaptchaV3Provider extends ReCaptchaV3Provider {
  private readonly hubTokenDeadline = createHubTokenDeadline();
  private readonly hubLazyStart = createLazyStart();

  initialize(app: FirebaseApp): void {
    const parentInitialize = (ReCaptchaV3Provider.prototype as unknown as ProviderInitialize).initialize;
    this.hubLazyStart.hold(() => parentInitialize.call(this, app));
  }

  getToken(...args: unknown[]): Promise<unknown> {
    if (this.hubLazyStart.release()) queueMicrotask(enableAppCheckAutoRefresh);
    const parentGetToken = (ReCaptchaV3Provider.prototype as unknown as ProviderTokenSource).getToken;
    if (args[0] === true) return parentGetToken.apply(this, args);
    return this.hubTokenDeadline.run(() => parentGetToken.apply(this, args));
  }
}

/**
 * UPDATE, speed plan 2026-10-19 (P2): reCAPTCHA IS NOW LOADED LATE, but App
 * Check itself is NOT deferred, which is what the reasoning below refused.
 * initializeAppCheck() still runs here at import time, so the `appCheck`
 * export is real from the first line and Firestore, Storage and Auth have
 * the provider from their first request (both objections, a and b, below
 * still hold and are still met). What moved is only the provider's own
 * initialize(), which is the step that adds Google's script: it now runs on
 * the provider's first getToken() (createLazyStart in
 * lib/appCheckDeadline.ts), and the background refresh starts then too. A
 * request that needs a token asks, the script loads once, the token waits
 * for it under the same deadline, the request goes out. The signed-out
 * home, a dancer's page and a returning person with a good saved token no
 * longer download it at all. Proven in tests/harness/app-check-deadline
 * (scenario D).
 *
 * PACKET J12 ITEM 4: DELIBERATELY NOT DEFERRED (history, read with the update above). The reCAPTCHA script this
 * pulls is the single biggest download of a cold open (345 KB, measured at
 * 323 to 674 ms against the live hub on 2026-09-10), so deferring it past
 * first paint looks like the obvious win. It was investigated and refused,
 * because it would change what App Check protects. Written down here rather
 * than only in a PR, so the next person does not have to find it twice.
 *
 * WHAT APP CHECK GATES TODAY, read from the code:
 *
 *   1. FIRESTORE AND CLOUD STORAGE. initializeAppCheck() registers the
 *      provider on the FirebaseApp; both SDKs then attach the token to
 *      their own requests. Whether an untokened request is REJECTED is a
 *      Firebase console setting, which is Bobbie only, and HUB-SETUP.md
 *      records it as monitor mode. It cannot be read from a container: a
 *      tokenless request gets PERMISSION_DENIED whether or not App Check is
 *      enforced. lib/appCheckDeadline.ts relies on monitor mode; read it
 *      before enforcing.
 *   2. FORTY NINE NETLIFY FUNCTIONS, through appCheckGuard()
 *      (netlify/functions/lib/appCheck.mjs). It rejects an INVALID token
 *      always, and a MISSING one when REQUIRE_FIREBASE_APPCHECK is set OR
 *      when the call site passes `{ required: true }`, which about forty
 *      six of them do. The token is attached client side by
 *      publicFunctionHeaders() and functionHeaders() in
 *      lib/functionSecurity.ts.
 *   3. NOT the dancer cookie check. dancerAuth.mjs and dancerPortal.mjs do
 *      not call appCheckGuard, and getDancerSessionStatus() sends no App
 *      Check header, so the boot's one unavoidable round trip is not
 *      behind App Check and deferring would not speed it up anyway.
 *
 * WHY DEFERRING BREAKS BOTH, proven by reading the shipped SDK rather than
 * assuming:
 *
 *   a. @firebase/firestore's FirebaseAppCheckTokenProvider.getToken()
 *      returns `Promise.resolve(null)` while its appCheck handle is still
 *      unset, and only picks the handle up through onInit or a
 *      setTimeout(0) probe. So every Firestore request issued inside a
 *      deferral window goes out with NO token. useUserDoc's onSnapshot on
 *      users/{uid} opens in the FIRST effect pass, which is inside any
 *      window that ends "after first paint" by definition.
 *   b. Worse, and permanently: `appCheck` below is a module const captured
 *      at import time. lib/functionSecurity.ts reads that same binding. If
 *      App Check were initialized later, that export would be null at
 *      import and STAY null, so every one of those forty nine functions
 *      would be called without a token for the whole session, not just for
 *      the first paint.
 *
 * The packet's own stop condition is "if deferring would let any Firestore
 * read or function call run without a token, do not defer". Both would. A
 * real win here is a smaller or later reCAPTCHA that App Check itself
 * arranges, or Firebase's own provider options, which is a separate packet
 * with a console change in it. Note also what the measurement showed: the
 * reCAPTCHA fetch starts after DOMContentLoaded and does NOT delay first
 * paint. It moves the `load` number, not the moment the page appears.
 */
function initializeHubAppCheck(app: FirebaseApp): {
  status: AppCheckStatus;
  instance: AppCheck | null;
} {
  if (typeof window === 'undefined') {
    return { status: { state: 'disabled', reason: 'server-render' }, instance: null };
  }

  const siteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY as string | undefined;
  if (!siteKey) {
    return { status: { state: 'disabled', reason: 'missing-site-key' }, instance: null };
  }

  const providerName = appCheckProviderName();
  const globalState = globalThis as typeof globalThis & {
    __ldgHubAppCheckReady?: AppCheckProvider;
    __ldgHubAppCheckInstance?: AppCheck;
  };

  if (globalState.__ldgHubAppCheckInstance) {
    return {
      status: { state: 'ready', provider: globalState.__ldgHubAppCheckReady ?? providerName },
      instance: globalState.__ldgHubAppCheckInstance,
    };
  }

  try {
    const instance = initializeAppCheck(app, {
      provider:
        providerName === 'recaptcha-v3'
          ? new BoundedReCaptchaV3Provider(siteKey)
          : new BoundedReCaptchaEnterpriseProvider(siteKey),
      // OFF at start, on at the first token request or a restored sign-in
      // (enableAppCheckAutoRefresh above). On here would fetch a token, and
      // load reCAPTCHA, for every visitor at import time.
      isTokenAutoRefreshEnabled: false,
    });
    globalState.__ldgHubAppCheckReady = providerName;
    globalState.__ldgHubAppCheckInstance = instance;
    return { status: { state: 'ready', provider: providerName }, instance };
  } catch (err) {
    return {
      status: {
        state: 'error',
        message: err instanceof Error ? err.message : String(err),
      },
      instance: null,
    };
  }
}

// Whether this browser will let Firestore keep its offline copy. Reading the
// localStorage getter is the test: it is what throws SecurityError when the
// browser refuses storage, and the SDK reads it later without a guard.
function persistentCacheUsable(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (!window.localStorage) return false;
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Firestore with an on-device cache instead of the default memory-only mode.
 *
 * Why this exists (Bobbie, 2026-07-28, staging on one-bar LTE): with no local
 * cache, EVERY page load waits on a live round trip before it can paint
 * anything, and on a bad connection that is a 30-second stall or a page that
 * never loads at all. With the persistent cache, data a device has seen
 * before renders immediately from IndexedDB while the network catches up in
 * the background - the realtime listeners then stream in anything newer.
 *
 * autoDetectLongPolling stays on (it is the SDK default) so networks that
 * silently kill streaming connections fall back to long polling on their own.
 *
 * The multi-tab manager keeps two open Hub tabs from fighting over the cache.
 * The SDK does NOT fall back to memory on its own when the browser refuses
 * storage outright. In WebKit with Safari's "Block All Cookies" on, the
 * window.localStorage getter itself throws SecurityError, and the SDK's
 * IndexedDB persistence reads localStorage unguarded: init "succeeds", the
 * first call ends in an unhandled SecurityError, and every later onSnapshot
 * throws "FIRESTORE INTERNAL ASSERTION FAILED" (ID b815), which a
 * subscription inside a React effect turns into AppErrorBoundary for any
 * signed-in person on that browser. Proven by running on 2026-09-24,
 * tests/harness/firestore-storage-blocked/check.mjs. So persistentCacheUsable()
 * is asked first, and a browser that refuses storage gets the memory cache:
 * everything works, just without the offline copy. The catch below is a
 * second belt for environments where init itself throws, so a cache problem
 * can never take down Firestore entirely.
 *
 * Security note: the document cache is per ORIGIN, not per authenticated
 * user - the SDK does NOT wall cached documents off between accounts. Two
 * things keep that honest here: every hook in this app queries with
 * role-provable filters, so a different signed-in user's queries only match
 * documents they could read anyway; and signOut() in lib/auth.ts terminates
 * Firestore and wipes the IndexedDB cache, so nothing a staff account loaded
 * survives sign-out on a shared device. If you change the sign-out flow,
 * keep that wipe.
 */
function initFirestoreWithCache(app: FirebaseApp): Firestore {
  try {
    return initializeFirestore(app, {
      localCache: persistentCacheUsable()
        ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
        : memoryLocalCache(),
    });
  } catch {
    return getFirestore(app);
  }
}

/**
 * The Helper's Realtime Database, which the Hub shares because both apps live
 * in the SAME Firebase project (ldg-helper). The Comp Day ticker on the comp
 * board reads ldg_shared/current from here.
 *
 * Hard-coded with an env override rather than a required env var, and
 * deliberately: making this a Netlify variable would mean the ticker silently
 * does nothing until somebody remembers to set it, on a site where the value
 * is a constant derived from the project id. The same constant is already
 * spelled out server-side in netlify/functions/syncCompToHelper.mjs; keep the
 * two in step.
 */
export const HELPER_DATABASE_URL =
  (import.meta.env.VITE_FIREBASE_DATABASE_URL as string | undefined)
  || 'https://ldg-helper-default-rtdb.firebaseio.com';

function initAuthWithoutEagerOAuth(app: FirebaseApp): Auth {
  try {
    // Keep getAuth's persistence order, but do not make repeat email sign-ins
    // wait for the OAuth iframe that Firebase eagerly opens on phones/Safari.
    // Actual popup/redirect operations supply their resolver in auth.ts.
    return initializeAuth(app, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ],
    });
  } catch (err) {
    // Hot reload can meet an Auth handle initialized by the previous module.
    // Only this exact SDK condition permits reuse; other setup failures remain
    // visible through buildHandles instead of silently choosing new defaults.
    if ((err as { code?: string } | null)?.code === 'auth/already-initialized') {
      return getAuth(app);
    }
    throw err;
  }
}

function buildHandles(): FirebaseHandles {
  // Collect env values up front so we can clearly report what's missing.
  const env = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  const missingKeys = (Object.entries(env) as Array<[string, string | undefined]>)
    .filter(([, v]) => !v)
    .map(([k]) => `VITE_FIREBASE_${k.replace(/([A-Z])/g, '_$1').toUpperCase()}`);

  if (missingKeys.length > 0) {
    return {
      app: null,
      appCheck: null,
      db: null,
      auth: null,
      storage: null,
      status: { state: 'missing-env', missingKeys },
    };
  }

  try {
    // Reuse the existing app instance if hot-reload re-runs this module.
    // databaseURL joins the config here rather than in `env` above so it can
    // never land in missingKeys: it always has a value, and a missing Realtime
    // Database URL must never be what stops the Hub from booting.
    //
    // authDomain follows the page on the Hub's own addresses, so the Google
    // sign-in helper is always same-origin there (lib/authSignInPolicy.ts,
    // SAME_ORIGIN_AUTH_HOSTS). The env value still has to be set, and it is
    // what deploy previews and localhost keep using.
    const authDomain = resolveAuthDomain({
      appHost: typeof window === 'undefined' ? '' : window.location.host,
      configuredAuthDomain: env.authDomain!,
    });
    const app = getApps()[0]
      ?? initializeApp({ ...env, authDomain, databaseURL: HELPER_DATABASE_URL });
    const appCheckSetup = initializeHubAppCheck(app);
    const appCheck = appCheckSetup.instance;
    const db = initFirestoreWithCache(app);
    const auth = initAuthWithoutEagerOAuth(app);
    const storage = getStorage(app);
    // A restored sign-in will need tokens all session: keep them fresh in the
    // background from here, as before. With a saved token still good this
    // only schedules the next refresh; it loads nothing now.
    if (appCheck && typeof auth.authStateReady === 'function') {
      void auth.authStateReady().then(() => {
        if (auth.currentUser) enableAppCheckAutoRefresh();
      }, () => undefined);
    }
    return {
      app,
      appCheck,
      db,
      auth,
      storage,
      status: { state: 'ready', projectId: env.projectId!, appCheck: appCheckSetup.status },
    };
  } catch (err) {
    return {
      app: null,
      appCheck: null,
      db: null,
      auth: null,
      storage: null,
      status: {
        state: 'error',
        message: err instanceof Error ? err.message : String(err),
      },
    };
  }
}

const handles = buildHandles();

export const app = handles.app;
export const appCheck = handles.appCheck;
export const db = handles.db;
export const auth = handles.auth;
export const storage = handles.storage;
export const firebaseStatus = handles.status;
